import { Box, Typography, Card, CardContent, Chip, Grid, Button, Tabs, Tab, List, ListItem, ListItemText, ListItemSecondaryAction, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Alert, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useMemo, useEffect } from 'react';
import { fetchTournament, fetchMatches, removePlayerFromTournament, startTournament, endTournament, resetTournament, deleteTournament, populateEliminationBracket, registerForTournament, unregisterFromTournament, User } from '../../services/api';
import apiClient from '../../services/api';
import { format } from 'date-fns';
import { ArrowBack as ArrowBackIcon, PersonAdd as PersonAddIcon, Delete as DeleteIcon, ExpandMore as ExpandMoreIcon, People as PeopleIcon, PersonAddAlt as PersonAddAltIcon } from '@mui/icons-material';
import { GENDER_LABELS, TOURNAMENT_TYPE_LABELS, TOURNAMENT_MIN_PLAYERS, TournamentType } from '../../constants/userConstants';
import EditTournamentDialog from '../../components/tournaments/EditTournamentDialog';
import AddPlayerDialog from '../../components/tournaments/AddPlayerDialog';
import ManageManagersDialog from '../../components/tournaments/ManageManagersDialog';
import BracketDisplay from '../../components/tournaments/BracketDisplay';
import PoolStandings from '../../components/tournaments/PoolStandings';
import MatchScoreModal from '../../components/matches/MatchScoreModal';
import { useAuth } from '../../contexts/AuthContext';
import { canManageTournament } from '../../utils/tournamentPermissions';

const TournamentDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addPlayerDialogOpen, setAddPlayerDialogOpen] = useState(false);
  const [manageManagersDialogOpen, setManageManagersDialogOpen] = useState(false);
  const [matchScoreDialogOpen, setMatchScoreDialogOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<any>(null);
  const { user } = useAuth();
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [playerToRemove, setPlayerToRemove] = useState<{ id: string; name: string } | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data: tournament, isLoading, error } = useQuery(
    ['tournament', id],
    () => fetchTournament(id!),
    { enabled: !!id }
  );

  const { data: matches, isLoading: matchesLoading, refetch: refetchMatches } = useQuery(
    ['matches', id],
    () => fetchMatches(id),
    { 
      enabled: !!id,
      refetchOnMount: 'always',
      staleTime: 0,
      onSuccess: (data) => {
        console.log('Matches fetched:', data);
      }
    }
  );

  const { data: eligibleUsersResponse } = useQuery(
    ['tournament', id, 'eligible-users'],
    () => apiClient.get<{ users: User[]; registered_id_to_user_id_map?: Record<string, string> }>(`/tournaments/${id}/eligible-users`),
    { 
      enabled: !!id,
      refetchOnMount: 'always',
      staleTime: 0,
      onSuccess: (response) => {
        const data = response.data;
        const users = Array.isArray(data) ? data : (data.users || []);
        console.log('Eligible users fetched:', users?.length, 'users');
        console.log('Eligible users:', users);
        if (!Array.isArray(data) && data.registered_id_to_user_id_map) {
          console.log('Registered ID to User ID mapping:', data.registered_id_to_user_id_map);
        }
      }
    }
  );

  // Extract users from response (handle both old array format and new object format)
  const eligibleUsers: User[] | undefined = eligibleUsersResponse?.data 
    ? (Array.isArray(eligibleUsersResponse.data) 
        ? eligibleUsersResponse.data 
        : (eligibleUsersResponse.data.users && Array.isArray(eligibleUsersResponse.data.users)
            ? eligibleUsersResponse.data.users
            : []))
    : undefined;
  
  const registeredIDToUserIDMap: Record<string, string> | undefined = eligibleUsersResponse?.data && !Array.isArray(eligibleUsersResponse.data)
    ? eligibleUsersResponse.data.registered_id_to_user_id_map
    : undefined;

  const removePlayerMutation = useMutation({
    mutationFn: (playerId: string) => removePlayerFromTournament(id!, playerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tournament', id] });
      queryClient.invalidateQueries({ queryKey: ['tournament', id, 'eligible-users'] });
      setRemoveDialogOpen(false);
      setPlayerToRemove(null);
    },
  });

  const startTournamentMutation = useMutation({
    mutationFn: () => startTournament(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tournament', id] });
      queryClient.invalidateQueries({ queryKey: ['matches', id] });
    },
  });

  const endTournamentMutation = useMutation({
    mutationFn: () => endTournament(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tournament', id] });
    },
  });

  const resetTournamentMutation = useMutation({
    mutationFn: () => resetTournament(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tournament', id] });
      queryClient.invalidateQueries({ queryKey: ['matches', id] });
    },
  });

  const deleteTournamentMutation = useMutation({
    mutationFn: () => deleteTournament(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tournaments'] });
      navigate('/tournaments');
    },
  });

  const populateBracketMutation = useMutation({
    mutationFn: () => populateEliminationBracket(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tournament', id] });
      queryClient.invalidateQueries({ queryKey: ['matches', id] });
    },
  });

  const handleRemoveClick = (playerId: string) => {
    const user = eligibleUsers?.find(u => u.id === playerId);
    if (user) {
      setPlayerToRemove({ id: playerId, name: user.name });
      setRemoveDialogOpen(true);
    }
  };

  const handleRemoveConfirm = () => {
    if (playerToRemove) {
      removePlayerMutation.mutate(playerToRemove.id);
    }
  };

  // Refetch eligible users when tournament registered players change
  useEffect(() => {
    if (tournament?.registered_player_ids && id) {
      queryClient.invalidateQueries({ queryKey: ['tournament', id, 'eligible-users'] });
    }
  }, [tournament?.registered_player_ids, id, queryClient]);

  const registeredPlayers = useMemo(() => {
    if (!tournament?.registered_player_ids || !Array.isArray(tournament.registered_player_ids)) {
      return [];
    }
    const registeredPlayerIds = tournament.registered_player_ids;
    
    // If we have eligibleUsers (must be an array), find registered players
    // Use the mapping from backend if available, otherwise fall back to matching logic
    if (eligibleUsers && Array.isArray(eligibleUsers)) {
      const foundUsers: User[] = [];
      const foundUserIDs = new Set<string>();
      
      // If we have the mapping from backend, use it (most reliable)
      if (registeredIDToUserIDMap && typeof registeredIDToUserIDMap === 'object') {
        for (const registeredID of registeredPlayerIds) {
          const userID = registeredIDToUserIDMap[registeredID];
          if (userID) {
            const user = eligibleUsers.find(u => u && u.id === userID);
            if (user && !foundUserIDs.has(user.id)) {
              foundUsers.push(user);
              foundUserIDs.add(user.id);
            }
          }
        }
      }
      
      // Fallback: Try matching by ID, email, or other methods
      if (foundUsers.length < registeredPlayerIds.length) {
        const registeredPlayerIdsSet = new Set(registeredPlayerIds);
        
        // First pass: Try exact ID match
        for (const user of eligibleUsers) {
          if (user && registeredPlayerIdsSet.has(user.id) && !foundUserIDs.has(user.id)) {
            foundUsers.push(user);
            foundUserIDs.add(user.id);
          }
        }
        
        // Second pass: For missing registered IDs, try to match by email
        const missingRegisteredIDs = registeredPlayerIds.filter(id => {
          if (registeredIDToUserIDMap && registeredIDToUserIDMap[id]) {
            return false; // Already matched via mapping
          }
          return !foundUsers.some(u => u && u.id === id);
        });
        
        for (const missingID of missingRegisteredIDs) {
          const matchedUser = eligibleUsers.find(user => {
            if (!user || foundUserIDs.has(user.id)) return false; // Already found
            // Try email match
            if (user.email === missingID) return true;
            // Try ID match (backwards compatibility)
            if (user.id === missingID) return true;
            return false;
          });
          
          if (matchedUser) {
            foundUsers.push(matchedUser);
            foundUserIDs.add(matchedUser.id);
            console.log('Matched registered player by fallback method:', {
              registeredID: missingID,
              matchedUserID: matchedUser.id,
              matchedUserEmail: matchedUser.email
            });
          }
        }
      }
      
      // Final validation
      if (foundUsers.length < registeredPlayerIds.length) {
        console.warn('Some registered players not found:', {
          foundCount: foundUsers.length,
          expectedCount: registeredPlayerIds.length,
          eligibleUsersCount: eligibleUsers.length,
          registeredPlayerIds,
          foundUserIDs: Array.from(foundUserIDs)
        });
      }
      
      console.log('Registered players filter result:', {
        eligibleUsersCount: eligibleUsers.length,
        registeredPlayerIds: registeredPlayerIds,
        registeredPlayerIdsCount: registeredPlayerIds.length,
        foundCount: foundUsers.length,
        usedMapping: !!registeredIDToUserIDMap,
        foundUsers: foundUsers.map(u => ({ id: u.id, name: u.name, email: u.email }))
      });
      
      return foundUsers;
    }
    
    // If eligibleUsers is not loaded yet or not an array, return empty array
    console.log('Registered players filter: eligibleUsers not loaded yet or invalid format', {
      registeredPlayerIdsCount: registeredPlayerIds.length,
      eligibleUsersType: typeof eligibleUsers,
      isArray: Array.isArray(eligibleUsers)
    });
    return [];
  }, [eligibleUsers, tournament?.registered_player_ids, registeredIDToUserIDMap]);

  const isRegistered = user?.userId && tournament?.registered_player_ids?.includes(user.userId);
  const canManage = canManageTournament(user, tournament || null);
  const isRegistrationOpen = tournament?.registration_start_date && tournament?.registration_end_date && 
    new Date() >= new Date(tournament.registration_start_date) && 
    new Date() < new Date(tournament.registration_end_date);

  const registerMutation = useMutation({
    mutationFn: () => registerForTournament(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tournament', id] });
      queryClient.invalidateQueries({ queryKey: ['tournament', id, 'eligible-users'] });
    },
  });

  const unregisterMutation = useMutation({
    mutationFn: () => unregisterFromTournament(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tournament', id] });
      queryClient.invalidateQueries({ queryKey: ['tournament', id, 'eligible-users'] });
    },
  });

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error loading tournament</div>;
  if (!tournament) return <div>Tournament not found</div>;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ongoing':
        return 'success';
      case 'upcoming':
        return 'info';
      case 'completed':
        return 'default';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/tournaments')}
        sx={{ mb: 2 }}
      >
        Back to Tournaments
      </Button>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            {tournament.name}
          </Typography>
          <Chip label={tournament.status} color={getStatusColor(tournament.status)} />
        </Box>
        {tournament.status === 'upcoming' && (() => {
          const minPlayers = TOURNAMENT_MIN_PLAYERS[tournament.type as TournamentType] || 2;
          const currentPlayers = tournament.registered_player_ids?.length || 0;
          const hasEnoughPlayers = currentPlayers >= minPlayers;
          
          return !hasEnoughPlayers && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Tournament requires at least {minPlayers} players to start. Currently have {currentPlayers} player{currentPlayers !== 1 ? 's' : ''}.
            </Alert>
          );
        })()}
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {canManage ? (
            <>
              {tournament.status === 'upcoming' && (() => {
                const minPlayers = TOURNAMENT_MIN_PLAYERS[tournament.type as TournamentType] || 2;
                const currentPlayers = tournament.registered_player_ids?.length || 0;
                const hasEnoughPlayers = currentPlayers >= minPlayers;
                
                return (
                  <Button 
                    variant="contained" 
                    color="success"
                    onClick={() => startTournamentMutation.mutate()}
                    disabled={startTournamentMutation.isPending || !hasEnoughPlayers}
                  >
                    {startTournamentMutation.isPending ? 'Starting...' : 'Start Tournament'}
                  </Button>
                );
              })()}
              {tournament.status === 'ongoing' && (
                <>
                  {tournament.type === 'round_robin_ladder' && (
                    <Button 
                      variant="contained" 
                      color="primary"
                      onClick={() => populateBracketMutation.mutate()}
                      disabled={populateBracketMutation.isPending}
                    >
                      {populateBracketMutation.isPending ? 'Populating...' : 'Populate Bracket'}
                    </Button>
                  )}
                  <Button 
                    variant="outlined" 
                    color="info"
                    onClick={() => resetTournamentMutation.mutate()}
                    disabled={resetTournamentMutation.isPending}
                  >
                    {resetTournamentMutation.isPending ? 'Resetting...' : 'Reset to Upcoming'}
                  </Button>
                  <Button 
                    variant="contained" 
                    color="warning"
                    onClick={() => endTournamentMutation.mutate()}
                    disabled={endTournamentMutation.isPending}
                  >
                    {endTournamentMutation.isPending ? 'Ending...' : 'End Tournament'}
                  </Button>
                </>
              )}
              <Button 
                variant="outlined" 
                startIcon={<PersonAddIcon />}
                onClick={() => setAddPlayerDialogOpen(true)}
                disabled={tournament.status !== 'upcoming'}
              >
                Add Player
              </Button>
              {(tournament.match_type_config === 'doubles' || tournament.match_type_config === 'mixed_doubles') && (
                <Button 
                  variant="outlined" 
                  startIcon={<PeopleIcon />}
                  onClick={() => navigate(`/tournaments/${id}/teams`)}
                >
                  Manage Teams
                </Button>
              )}
              <Button 
                variant="contained" 
                onClick={() => setEditDialogOpen(true)}
                disabled={tournament.status !== 'upcoming'}
              >
                Edit Tournament
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<PeopleIcon />}
                onClick={() => setManageManagersDialogOpen(true)}
              >
                Manage Managers
              </Button>
              {user?.['custom:role'] === 'admin' && (
                <Button 
                  variant="outlined" 
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  Delete Tournament
                </Button>
              )}
            </>
          ) : (
            // Regular user view - show Join/Leave Tournament button
            user && tournament.status === 'upcoming' && isRegistrationOpen && (
              <>
                {isRegistered ? (
                  <Button 
                    variant="outlined" 
                    color="error"
                    onClick={() => unregisterMutation.mutate()}
                    disabled={unregisterMutation.isPending}
                  >
                    {unregisterMutation.isPending ? 'Leaving...' : 'Leave Tournament'}
                  </Button>
                ) : (
                  <Button 
                    variant="contained" 
                    color="primary"
                    startIcon={<PersonAddAltIcon />}
                    onClick={() => registerMutation.mutate()}
                    disabled={registerMutation.isPending}
                  >
                    {registerMutation.isPending ? 'Joining...' : 'Join Tournament'}
                  </Button>
                )}
              </>
            )
          )}
        </Box>
      </Box>

      <EditTournamentDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        tournament={tournament}
      />

      <AddPlayerDialog
        open={addPlayerDialogOpen}
        onClose={() => setAddPlayerDialogOpen(false)}
        tournament={tournament}
      />

      <ManageManagersDialog
        open={manageManagersDialogOpen}
        onClose={() => setManageManagersDialogOpen(false)}
        tournament={tournament}
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Tournament Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Tournament Type
                  </Typography>
                  <Typography variant="body1">
                    {TOURNAMENT_TYPE_LABELS[tournament.type as TournamentType]}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Location
                  </Typography>
                  <Typography variant="body1">
                    {tournament.location}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Dates
                  </Typography>
                  <Typography variant="body1">
                    {format(new Date(tournament.start_date), 'MMM dd, yyyy')} -{' '}
                    {format(new Date(tournament.end_date), 'MMM dd, yyyy')}
                  </Typography>
                </Grid>
                {tournament.description && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Description
                    </Typography>
                    <Typography variant="body1">{tournament.description}</Typography>
                  </Grid>
                )}
                {(tournament.registration_start_date || tournament.registration_end_date) && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Registration Period
                    </Typography>
                    <Typography variant="body1">
                      {tournament.registration_start_date && format(new Date(tournament.registration_start_date), 'MMM dd, yyyy')}
                      {tournament.registration_start_date && tournament.registration_end_date && ' - '}
                      {tournament.registration_end_date && format(new Date(tournament.registration_end_date), 'MMM dd, yyyy')}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {(tournament.allowed_genders || tournament.min_level || tournament.max_level) && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Eligibility Requirements
                </Typography>
                <Grid container spacing={2}>
                  {tournament.allowed_genders && tournament.allowed_genders.length > 0 && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Allowed Genders
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        {tournament.allowed_genders.map((gender) => (
                          <Chip
                            key={gender}
                            label={GENDER_LABELS[gender]}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Grid>
                  )}
                  {(tournament.min_level || tournament.max_level) && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        USTA Level Range
                      </Typography>
                      <Typography variant="body1">
                        {tournament.min_level || 'Any'} - {tournament.max_level || 'Any'}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          )}

          <Card>
            <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
              <Tab label="Matches" />
              <Tab label="Bracket" />
              {tournament.type === 'round_robin_ladder' && <Tab label="Pool Standings" />}
            </Tabs>
            <CardContent>
              {activeTab === 0 && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      Matches
                    </Typography>
                    <Button 
                      size="small" 
                      onClick={() => refetchMatches()}
                      disabled={matchesLoading}
                    >
                      {matchesLoading ? 'Loading...' : 'Refresh'}
                    </Button>
                  </Box>
                  {matchesLoading ? (
                    <Typography color="text.secondary">Loading matches...</Typography>
                  ) : matches && matches.length > 0 ? (() => {
                    console.log('Rendering matches:', matches.length, matches);
                    // Group matches by pool
                    const poolGroups = matches.reduce((acc: any, match: any) => {
                      const poolName = match.pool_name || 'Elimination';
                      if (!acc[poolName]) acc[poolName] = [];
                      acc[poolName].push(match);
                      return acc;
                    }, {});
                    
                    return Object.entries(poolGroups).map(([poolName, poolMatches]: [string, any]) => (
                      <Accordion key={poolName} defaultExpanded sx={{ mb: 2 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {poolName} ({poolMatches.length} matches)
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          {poolMatches.map((match: any) => {
                          const isPlayer = user && (match.player1_id === user.userId || match.player2_id === user.userId);
                          const isAdmin = user?.email?.includes('admin');
                          const canClick = (isPlayer && match.status === 'scheduled') || isAdmin;
                          
                          // Get player names
                          const player1 = eligibleUsers?.find(u => u.id === match.player1_id);
                          const player2 = eligibleUsers?.find(u => u.id === match.player2_id);
                          const player1Name = player1?.name || 'TBD';
                          const player2Name = player2?.name || 'TBD';
                          
                          return (
                            <Card 
                              key={match.id} 
                              sx={{ 
                                mb: 2,
                                cursor: canClick ? 'pointer' : 'default',
                                '&:hover': canClick ? {
                                  boxShadow: 3,
                                  transform: 'translateY(-2px)',
                                  transition: 'all 0.2s'
                                } : {}
                              }}
                              onClick={() => {
                                if (canClick) {
                                  setSelectedMatch(match);
                                  setMatchScoreDialogOpen(true);
                                }
                              }}
                            >
                              <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Box>
                                    <Typography variant="body1" fontWeight="medium">
                                      Match {match.match_number}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {player1Name} vs {player2Name}
                                    </Typography>
                                  </Box>
                                  <Chip 
                                    label={match.status} 
                                    size="small"
                                    color={match.status === 'completed' ? 'success' : 'default'}
                                  />
                                </Box>
                                {match.score && (
                                  <Typography variant="body2" sx={{ mt: 1 }}>
                                    Score: {match.score.sets.map((set: any) => 
                                      `${set.player1_games}-${set.player2_games}`
                                    ).join(', ')}
                                  </Typography>
                                )}
                                {canClick && (
                                  <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                                    Click to enter score
                                  </Typography>
                                )}
                              </CardContent>
                            </Card>
                          );
                        })}
                        </AccordionDetails>
                      </Accordion>
                    ));
                  })() : (
                    <Typography color="text.secondary">No matches scheduled yet</Typography>
                  )}
                </Box>
              )}
              {activeTab === 1 && (
                <Box>
                  {tournament.bracket && eligibleUsers ? (
                    <BracketDisplay bracket={tournament.bracket} users={eligibleUsers} tournament={tournament} />
                  ) : tournament.status === 'upcoming' ? (
                    <Typography color="text.secondary">
                      Bracket will be generated when the tournament starts
                    </Typography>
                  ) : (
                    <Typography color="text.secondary">
                      No bracket available for this tournament
                    </Typography>
                  )}
                </Box>
              )}
              {activeTab === 2 && tournament.type === 'round_robin_ladder' && (
                <Box>
                  {tournament.bracket && eligibleUsers ? (
                    <PoolStandings bracket={tournament.bracket} users={eligibleUsers} matches={matches} />
                  ) : (
                    <Typography color="text.secondary">
                      Pool standings will be available after the tournament starts
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Stats
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Registered Players
                </Typography>
                <Typography variant="h4">{tournament?.registered_player_ids?.length || 0}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Total Matches
                </Typography>
                <Typography variant="h4">{matches?.length || 0}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Completed
                </Typography>
                <Typography variant="h4">
                  {matches?.filter((m) => m.status === 'completed').length || 0}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Upcoming
                </Typography>
                <Typography variant="h4">
                  {matches?.filter((m) => m.status === 'scheduled').length || 0}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Registered Players
              </Typography>
              {registeredPlayers.length > 0 ? (
                <List dense>
                  {registeredPlayers.map((player) => (
                    <ListItem key={player.id}>
                      <ListItemText
                        primary={player.name}
                        secondary={`${player.email}${player.usta_level ? ` • ${player.usta_level}` : ''}`}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          aria-label="remove"
                          onClick={() => handleRemoveClick(player.id)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary" variant="body2">
                  No players registered yet
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={removeDialogOpen} onClose={() => setRemoveDialogOpen(false)}>
        <DialogTitle>Remove Player</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove {playerToRemove?.name} from this tournament?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleRemoveConfirm} 
            color="error" 
            variant="contained"
            disabled={removePlayerMutation.isPending}
          >
            {removePlayerMutation.isPending ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Tournament</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{tournament?.name}"? This action cannot be undone and will delete all associated matches.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={() => {
              deleteTournamentMutation.mutate();
              setDeleteDialogOpen(false);
            }} 
            color="error" 
            variant="contained"
            disabled={deleteTournamentMutation.isPending}
          >
            {deleteTournamentMutation.isPending ? 'Deleting...' : 'Delete Tournament'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {selectedMatch && (
        <MatchScoreModal
          open={matchScoreDialogOpen}
          onClose={() => {
            setMatchScoreDialogOpen(false);
            setSelectedMatch(null);
          }}
          match={selectedMatch}
          users={eligibleUsers || []}
          thirdSetFormat={tournament?.third_set_format as 'full_set' | 'match_tiebreak'}
        />
      )}
    </Box>
  );
};

export default TournamentDetailPage;
